import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { PenIcon, DownloadIcon, DatabaseIcon } from '@ofself/shell'
import { api } from './api.js'
import { qh } from './qh.js'
import { downloadJson, downloadMarkdownFiles, parseMarkdownWord, parseJsonWords, readText } from './transfer.js'

/* THE DICTIONARY'S SETTINGS ARE PANES IN THE SHELL'S WINDOW (owner call
   2026-09-19: "refactor the settings panel to use the shell level theme, rip
   out the current styling — see how other apps do settings"). The bespoke
   Modal is gone. The rail's cog opens the kit's own Settings window — the
   fleet's System sections above, these panes below — exactly as ink mounts
   INK_SETTINGS through <Shell settings={…}>. Appearance (the skin, the dot
   grid, the cells) is the shell's and is not repeated here. EVERY PANE IS
   BLOCKS (PaneBlocks.css, owner call 2026-09-23: "same for all settings
   pages"): a block is its option, lit while on; what it does is its Quick
   Help; a count or a result is its one quiet note.

   A preference about how the app behaves while you type belongs on THIS
   machine and in front of you, not in the graph: it is not a fact about your
   dictionary, it is a fact about how you like to work, and it should not
   travel to someone you share a word with. */
export const PREFS_KEY = 'dictionary:prefs'
export const DEFAULT_PREFS = { termSuggest: true, termMarks: true, catchAvoided: true }

export function readPrefs() {
  try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') } }
  catch { return { ...DEFAULT_PREFS } }
}
export function writePrefs(next) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)) } catch { /* private window */ }
  return next
}

/* ONE STORE FOR THE PREFS. The panes are static nodes handed to the shell at
   mount, so they cannot take props from App's render — they subscribe here,
   and App reads the same source, so a switch flipped in the window is felt
   by the editor on the next frame. */
let current = readPrefs()
const listeners = new Set()
export function savePrefs(next) { current = writePrefs({ ...current, ...next }); listeners.forEach(l => l()) }
export function usePrefs() {
  const prefs = useSyncExternalStore(l => { listeners.add(l); return () => listeners.delete(l) }, () => current)
  return [prefs, savePrefs]
}

const WRITING = [
  { key: 'termSuggest', label: 'Suggest my words', on: 'On: a committed word offers itself as you begin typing it; Tab takes it. Kept on this device only. Press to turn it off.',
    off: 'Off: no dropdown while you type. Press to turn it back on.' },
  { key: 'termMarks', label: 'Underline my words', on: 'On: a dotted rule under every committed word, wherever it is written; hover one for its definition. Kept on this device only. Press to turn it off.',
    off: 'Off: your words are not marked. Press to turn the rule back on.' },
  { key: 'catchAvoided', label: 'Anti-words', on: 'On: a red dotted rule under a word from your anti-dictionaries; hover it for why you avoid it and the words you would rather use. Kept on this device only. Press to turn it off.',
    off: 'Off: anti-words are not marked. Press to turn the catch back on.' },
]
function WritingPane() {
  const [prefs, save] = usePrefs()
  return <div className="ofself-pane-blocks">
    {WRITING.map(w => {
      const on = prefs[w.key] !== false
      return <button key={w.key} type="button" className="ofself-pane-block" aria-pressed={on} onClick={() => save({ [w.key]: !on })}
        {...qh(w.label, on ? w.on : w.off)}>
        <span className="ofself-pane-block__title">{w.label}</span>
      </button>
    })}
  </div>
}

/* THE ABSORBER (owner call 2026-09-18): per app, the Lexicographer reads what
   you have written and proposes the words you lean on — in your sense, quoted
   where each was met. They land as PROPOSED; you commit or delete them. The
   list reloads through one event, because a pane handed to the shell at mount
   cannot be passed App's loader. */
export const ABSORBED_EVENT = 'dictionary:absorbed'
function AbsorbPane() {
  const [rows, setRows] = useState(null)
  const [busy, setBusy] = useState('')
  const [said, setSaid] = useState({})
  const load = () => api.absorbSources().then(r => setRows(r.sources || [])).catch(e => setRows({ error: e.message }))
  useEffect(() => { load() }, [])
  const run = async (key) => {
    setBusy(key); setSaid(s => ({ ...s, [key]: '' }))
    try {
      const r = await api.absorb(key)
      const n = (r.proposed || []).length
      setSaid(s => ({ ...s, [key]: n ? `${n} word${n === 1 ? '' : 's'} proposed from ${r.read} record${r.read === 1 ? '' : 's'}` : `nothing new in ${r.read} record${r.read === 1 ? '' : 's'}` }))
      if (n) window.dispatchEvent(new CustomEvent(ABSORBED_EVENT))
      load()
    } catch (e) {
      if (e.status === 428) setSaid(s => ({ ...s, [key]: 'the Lexicographer needs authorizing once: a tab opened, then press again' }))
      else setSaid(s => ({ ...s, [key]: e.message }))
    } finally { setBusy('') }
  }
  const count = r => r.error || `${r.readable ?? '0'} readable · ${r.absorbed} absorbed`
  return <div className="ofself-pane-blocks">
    {!Array.isArray(rows) && <div className="ofself-pane-block ofself-pane-block--static">
      <span className="ofself-pane-block__title">{rows?.error ? 'Unreadable' : 'Reading your apps'}</span>
      {rows?.error && <span className="ofself-pane-block__note">{rows.error}</span>}
    </div>}
    {Array.isArray(rows) && rows.map(r => <button key={r.key} type="button" className="ofself-pane-block"
      aria-busy={busy === r.key || undefined} disabled={!!busy || !!r.error || !r.readable} onClick={() => run(r.key)}
      {...qh(`Absorb ${r.label}`, `${r.note ? `${r.note} ` : ''}The Lexicographer reads the records it has not seen yet and proposes the words you lean on in them, in your sense, quoted where each was met. They arrive as proposed; you commit them. The first run may open a tab to authorize it once.`)}>
      <span className="ofself-pane-block__title">{r.label}</span>
      <span className="ofself-pane-block__note" role="status">{busy === r.key ? 'reading…' : said[r.key] || count(r)}</span>
    </button>)}
  </div>
}

/* THE DATA PANE (owner call 2026-09-19): the whole dictionary out, as one
   JSON or as a Markdown file per word; and in, from .md or .json files. An
   import never overwrites — a name already in the dictionary is skipped and
   said so — and the list reloads through the same event Absorb uses. */
function DataPane() {
  const fileRef = useRef(null)
  const [busy, setBusy] = useState('')
  const [said, setSaid] = useState({})
  const all = async () => ((await api.words()).words || []).filter(w => String(w.name || '').trim())
  const exportJson = async () => {
    setBusy('json'); setSaid({})
    try { const ws = await all(); downloadJson(ws); setSaid({ key: 'json', text: `${ws.length} word${ws.length === 1 ? '' : 's'} exported` }) }
    catch (e) { setSaid({ key: 'json', text: `could not export: ${e.message}` }) } finally { setBusy('') }
  }
  const exportMd = async () => {
    setBusy('md'); setSaid({})
    try { const ws = await all(); await downloadMarkdownFiles(ws); setSaid({ key: 'md', text: `${ws.length} file${ws.length === 1 ? '' : 's'} exported` }) }
    catch (e) { setSaid({ key: 'md', text: `could not export: ${e.message}` }) } finally { setBusy('') }
  }
  const importFiles = async (files) => {
    if (!files.length) return
    setBusy('import'); setSaid({ key: 'import', text: `reading ${files.length} file${files.length === 1 ? '' : 's'}…` })
    try {
      const have = new Set((await all()).map(w => w.name.trim().toLowerCase()))
      const incoming = []
      for (const f of files) {
        const text = await readText(f)
        if (/\.json$/i.test(f.name)) incoming.push(...parseJsonWords(text))
        else incoming.push(parseMarkdownWord(text, f.name))
      }
      let made = 0, skipped = 0, failed = 0
      // TWO PASSES: every word first, THEN the links between them — a synonym
      // linked before its own file is read would be minted as a stub, and the
      // file that defines it then refused as already here
      const born = []
      for (const w of incoming) {
        const key = w.name.toLowerCase()
        if (have.has(key)) { skipped++; continue }
        const { refs, synonyms = [], antonyms = [], ...fields } = w   // aliases ride in fields: they are the word's own   // the references were read back to plain words; the pairs are not a field
        try { const r = await api.create(fields); have.add(key); made++; born.push({ id: r.word.id, synonyms, antonyms }) } catch { failed++ }
      }
      for (const b of born) for (const [kind, list] of [['synonym', b.synonyms], ['antonym', b.antonyms]]) {
        for (const term of list) await api.link(b.id, { kind, term }).catch(() => {})
      }
      setSaid({ key: 'import', text: [`${made} imported`, skipped && `${skipped} already here, skipped`, failed && `${failed} refused by the server`].filter(Boolean).join(' · ') })
      if (made) window.dispatchEvent(new CustomEvent(ABSORBED_EVENT))
    } catch (e) { setSaid({ key: 'import', text: `could not import: ${e.message}` }) } finally { setBusy('') }
  }
  const block = (key, label, help, act) => <button type="button" className="ofself-pane-block" aria-busy={busy === key || undefined}
    disabled={!!busy} onClick={act} {...qh(label, help)}>
    <span className="ofself-pane-block__title">{label}</span>
    {said.key === key && <span className="ofself-pane-block__note" role="status">{said.text}</span>}
  </button>
  return <div className="ofself-pane-blocks">
    {block('json', 'Export as JSON', 'The whole dictionary as one file, every field: the lossless copy.', exportJson)}
    {block('md', 'Export as Markdown', 'One .md per word: the facts as frontmatter, the word as the heading, the definition as prose. The browser may ask once to allow several downloads.', exportMd)}
    {block('import', 'Import', 'Markdown (.md, one word per file; a heading or a word: line names it) or JSON (this app’s export, or an array of { name, text, tags }). Words you already have are skipped.', () => fileRef.current?.click())}
    <input ref={fileRef} type="file" accept=".md,.markdown,.txt,.json,application/json,text/markdown" multiple hidden
      onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; importFiles(fs) }} />
  </div>
}

/* the dictionary's panes for the shell window: pass to <Shell settings={…}>; the section glyphs are the kit's */
export const DICT_SETTINGS = [
  { id: 'dictionary-writing', label: 'Writing', icon: <PenIcon size={16} />, content: <WritingPane /> },
  { id: 'dictionary-absorb', label: 'Absorb', icon: <DownloadIcon size={16} />, content: <AbsorbPane /> },
  { id: 'dictionary-data', label: 'Data', icon: <DatabaseIcon size={16} />, content: <DataPane /> },
]
